import { Flex, Spinner, Callout, Select } from '@radix-ui/themes'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SiteWithStations } from '~/features/sites/service'
import { EmptyState } from '~/components/EmptyState'
import { LuChartColumnBig as LuBarChartBig } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { GoSquareFill } from 'react-icons/go'
import { ComboChart } from '~/components/Charts/ComboChart'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { formatNumber } from '~/utils/formating'
import { SectionCard } from '~/components/SectionCard'
import { BudgetGoalSchema } from '../schemas'
import { getMetric } from '~/features/dashboarding/service'
import { GOAL_MEASURE_UNIT } from '../constants'
import { useTRPC } from '~/utils/trpc'
import { Resolution } from '@willie/schemas'
import { formatDateLabel } from '~/features/dashboarding/utils'
import { PeriodSelector, PeriodSelection } from '~/components/PeriodSelector'
import ResolutionSelector from '~/components/ResolutionSelector'
import { DateRange } from '~/types/type-utils'
import { ViewOptionsPopover } from '~/features/dashboarding/components/ViewOptionsPopover'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import { useChartExportCSV, ChartExportHandle } from '~/features/dashboarding/hooks/useChartExportCSV'
import { ChartSectionMoreOptionsMenu } from '~/features/dashboarding/components/MoreOptionsDropdownMenu'
import { useChartExportImage } from '~/features/dashboarding/hooks/useChartExportImage'
import { ChartVignette, ChartVignettes } from '~/features/dashboarding/components/ChartVignette'

type BudgetChartDataPoint = {
  date: string
  measuredCost: number
  invoiceCost: number
  objectiveCost: number
  cumulativeCost: number
}

type MeasureCostDataPoint = Pick<BudgetChartDataPoint, 'measuredCost'>
// type InvoiceCostDataPoint = Pick<BudgetChartDataPoint, 'invoiceCost'>
type ObjectiveCostDataPoint = Pick<BudgetChartDataPoint, 'objectiveCost'>

type ToDateDataPoint =
  | (Omit<BudgetChartDataPoint, 'date'> & {
      diffPercent: number
      remainingPercent: number
    })
  | null

// const getBudgetValueAs = (item: { cost: number; consumption: number }, viewAs: BudgetGoalSchema['measure']) => {
//   return viewAs === 'currency' ? item.cost : item.consumption
// }

const makeInitialBudgetChartData = (
  startDate: DateTime,
  endDate: DateTime,
  res: Resolution
): Record<string, BudgetChartDataPoint> => {
  const nPeriods = Math.round(endDate.endOf(res).diff(startDate.startOf(res), res)[`${res}s`])
  return Array.from({ length: nPeriods + 1 }, (_, i) => {
    const date = startDate.plus({ [res]: i }).startOf(res)
    return {
      date: date.toISODate()!,
      measuredCost: 0,
      invoiceCost: 0,
      objectiveCost: 0,
      cumulativeCost: 0
    } satisfies BudgetChartDataPoint
  }).reduce((acc, point) => ({ ...acc, [point.date]: point }), {})
}

const BUDGET_VIEW_MODES = ['cumulative', 'periodic'] as const
type BudgetViewMode = (typeof BUDGET_VIEW_MODES)[number]

type Props = {
  site: SiteWithStations
  goal: BudgetGoalSchema
  showTitle?: boolean
}

const BudgetChart = ({ site, goal, ...props }: Props) => {
  const { t } = useTranslation('sites')
  const currentLanguage = useCurrentLanguage()

  // Add local state for period and resolution
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>('custom')
  const [resolution, setResolution] = useState<Resolution>(goal.resolution)
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: DateTime.fromJSDate(goal.startDate),
    endDate: DateTime.fromJSDate(goal.endDate)
  })
  const [viewMode, setViewMode] = useState<BudgetViewMode>('cumulative')

  const handlePeriodChange = (newDateRange: DateRange, newPeriod: PeriodSelection) => {
    setDateRange(newDateRange)
    setPeriodSelection(newPeriod)
  }

  // Get the stations based on goal scope (either direct stations or metric-based)
  const { data: metric } = useSuspenseQuery({
    queryKey: ['metric', goal.metricID],
    queryFn: () => {
      if (!goal.metricID) {
        return null
      }
      return getMetric({ metricID: goal.metricID })
    }
  })

  // Determine which stations to use based on goal configuration
  const stationsToUse = useMemo(() => {
    if (goal.metricID && metric) {
      return site.stations.filter((station) => metric.stationOperations.find((op) => op.stationId === station.id))
    } else {
      // Otherwise use all stations with cost from the site
      return site.stations.filter((station) => station.id === goal.stationID)
    }
  }, [site.stations, goal.metricID, metric])

  // Use dateRange and resolution from state instead of goal's defaults
  const startDate = dateRange.startDate
  const endDate = dateRange.endDate.endOf(resolution)

  const trpc = useTRPC()

  const { data: consumptionData = [], isPending: consumptionPending } = useQuery(
    trpc.stations.getStationsConsumptionData.queryOptions({
      stationIDs: stationsToUse.filter((s) => s.cost !== null).map((s) => s.id!),
      startDate: startDate.toISO()!,
      endDate: endDate.toISO()!,
      resolution: resolution
    })
  )

  // const { data: invoices, isPending: invoicesPending } = useQuery({
  //   queryKey: ['site-invoices', site.id],
  //   queryFn: () => listSiteBudgetItems({ siteID: site.id, type: 'historical' })
  // })

  const measuredCostData = useMemo(() => {
    if (!consumptionData) {
      return {}
    }

    const stationData: Record<string, MeasureCostDataPoint> = _(consumptionData)
      .flatMap((stationDataSet) =>
        stationDataSet.datapoints.map((point) => {
          const station = stationsToUse.find((s) => s.id === stationDataSet.stationID)
          if (!station) {
            return []
          }
          return {
            ...point,
            consumption: point.consumption * (goal.measure === 'volume' ? 1 : (station.cost ?? 0))
          }
        })
      )
      .groupBy('dateTime')
      .mapValues((values) => ({ measuredCost: _.sumBy(values, 'consumption') }) satisfies MeasureCostDataPoint)
      .value()

    return stationData
  }, [consumptionData, site])

  // const invoiceData = useMemo(() => {
  //   if (!invoices) {
  //     return {}
  //   }

  //   const data: Record<string, InvoiceCostDataPoint> = _(invoices)
  //     .map((i) => ({
  //       ...i,
  //       startDate: DateTime.fromJSDate(i.startDate).startOf('month'),
  //       endDate: DateTime.fromJSDate(i.endDate).endOf('month')
  //     }))
  //     .flatMap((i) => {
  //       const months = i.endDate.diff(i.startDate, 'months').months
  //       const monthlyBudget = getBudgetValueAs(i, goal.measure) / months

  //       // split each invoice into distinct months and spread the cost over the months
  //       return _.range(months).map((month) => ({
  //         date: i.startDate.plus({ month }).startOf('month').toISODate(),
  //         value: monthlyBudget * (month + 1) // cumulative cost
  //       }))
  //     })
  //     .groupBy('date')
  //     .mapValues((values) => ({ invoiceCost: _.sumBy(values, 'value') }) satisfies InvoiceCostDataPoint)
  //     .value()

  //   return data
  // }, [invoices])

  const objectiveData = useMemo(() => {
    const start = startDate.startOf(resolution)
    const end = endDate.endOf(resolution)

    const nperiods = Math.round(end.diff(start, resolution)[`${resolution}s`])
    const periodicBudget = goal.goal / nperiods

    const datapoints = _.range(nperiods)
      .map((period) => ({
        date: start.plus({ [resolution]: period }).toISODate()!,
        value: periodicBudget * (viewMode === 'cumulative' ? period + 1 : 1) // cumulative | periodic cost
      }))
      .reduce(
        (acc, point) => ({
          ...acc,
          [point.date]: { objectiveCost: point.value } satisfies ObjectiveCostDataPoint
        }),
        {}
      )

    return datapoints
  }, [goal, startDate, endDate, resolution, viewMode])

  // combine the data from the invoices and the consumption data
  const InitialBudgetData = makeInitialBudgetChartData(startDate, endDate, resolution)
  const combined: BudgetChartDataPoint[] = _(InitialBudgetData)
    .merge(measuredCostData)
    // .merge(invoiceData)
    .merge(objectiveData)
    .toPairs()
    .map(([date, curr], idx, all) => {
      const cumulativeCost =
        DateTime.fromISO(date) <= DateTime.now().startOf(resolution)
          ? _(all)
              .slice(0, idx + 1)
              .sumBy(([_, p]) => p.measuredCost)
          : 0

      return [date, { ...curr, cumulativeCost }] satisfies [string, BudgetChartDataPoint]
    })
    .fromPairs()
    .filter((d) => DateTime.fromISO(d.date) < endDate)
    .value()

  const chartData = combined.map((d) => ({
    date: formatDateLabel(d.date, goal.resolution, currentLanguage, combined.length),
    [t('budget.chart.forecastedCosts')]: _.round(viewMode === 'cumulative' ? d.cumulativeCost : d.measuredCost, 2),
    // [t('budget.chart.invoiceCosts')]: _.round(d.invoiceCost, 2),
    [t('budget.chart.objectiveCosts')]: _.round(d.objectiveCost, 2)
  }))

  const currentPeriod = combined.find((d) => d.date === DateTime.now().startOf(resolution).toISODate())
  const toDate: ToDateDataPoint = !currentPeriod
    ? null
    : {
        measuredCost: currentPeriod.measuredCost,
        invoiceCost: currentPeriod.invoiceCost,
        objectiveCost: currentPeriod.objectiveCost,
        remainingPercent: Math.round(_.clamp(1 - currentPeriod.cumulativeCost / goal.goal, 0, 1) * 100),
        diffPercent: Math.round(
          ((currentPeriod?.measuredCost - currentPeriod?.objectiveCost) / currentPeriod?.objectiveCost) * 100
        ),
        cumulativeCost: currentPeriod.cumulativeCost
      }

  const remainingDays = Math.floor(DateTime.fromJSDate(goal.endDate).diffNow('days').days)

  const exportFilename = `budget_${site.name}_${goal.id.slice(0, 8)}_${dateRange.startDate.toISODate()}_${dateRange.endDate.toISODate()}`

  const csvExportRef = useRef<ChartExportHandle>({ getExportData: () => [] })
  const handleExportCSV = useChartExportCSV(csvExportRef, `${exportFilename}.csv`)

  useEffect(() => {
    csvExportRef.current.getExportData = () => chartData
  }, [chartData])

  const { ref, handleExportImage: handleDivDownload, isExporting } = useChartExportImage(`${exportFilename}.png`)

  if (consumptionPending) {
    return (
      <SectionCard title={props.showTitle ? t('budget.overview.title') : ''}>
        <Flex align={'center'} justify={'center'} height={'400px'}>
          <Spinner size={'3'} />
        </Flex>
      </SectionCard>
    )
  }

  if (stationsToUse.length === 0) {
    return (
      <SectionCard title="" className="h-[400px]">
        <EmptyState
          title={t('budget.overview.emptyState.title')}
          description={t('budget.overview.emptyState.description')}
          afterBody={<LuBarChartBig size={36} color="var(--mint-11)" />}
        />
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title={props.showTitle ? t('budget.overview.title') : ''}
      className="min-h[400px]"
      innerRef={ref}
      secondaryActions={
        <Flex gap={'2'} align={'center'} className="no-export">
          <ViewOptionsPopover>
            <FieldWrapper label={'Période'}>
              <PeriodSelector
                size={'2'}
                periodSelection={periodSelection}
                dateRange={dateRange}
                onPeriodChange={handlePeriodChange}
              />
            </FieldWrapper>
            <FieldWrapper label={'Résolution'}>
              <ResolutionSelector
                size={'2'}
                defaultResolution={resolution}
                onChange={setResolution}
                omit={['hour']}
              />
            </FieldWrapper>
            <FieldWrapper label={t('stations.leakSection.chart.viewMode')}>
              <Select.Root
                value={viewMode}
                onValueChange={(value) => setViewMode(value as BudgetViewMode)}
                size="2"
              >
                <Select.Trigger />
                <Select.Content color={'gray'} variant="soft">
                  {BUDGET_VIEW_MODES.map((mode) => (
                    <Select.Item key={mode} value={mode}>
                      {t(`budget.chart.viewAs.${mode}`)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FieldWrapper>
          </ViewOptionsPopover>
          <ChartSectionMoreOptionsMenu
            handleExportImage={handleDivDownload}
            handleExportCSV={handleExportCSV}
            isExporting={isExporting}
            linkTo="/sites/$siteID/config/budget/$goalID"
            linkParams={{ siteID: site.id, goalID: goal.id }}
            linkText={t('budget.objectives.configureObjective')}
          />
        </Flex>
      }
    >
      <Flex direction={'column'} gap={'5'} p={'4'}>
        {_.isNumber(toDate?.diffPercent) && toDate?.diffPercent >= 0 && (
          <Callout.Root color={toDate.remainingPercent === 0 ? 'red' : 'amber'} className="mt-4 no-export">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              {toDate.remainingPercent === 0 ? (
                <>{t('budget.chart.surpassedBudgetAlert')}.</>
              ) : (
                <>{t('budget.chart.budgetWarningAlert', { diffPercent: toDate.diffPercent })}</>
              )}
            </Callout.Text>
          </Callout.Root>
        )}
        <ChartVignettes>
          <ChartVignette
            icon={<GoSquareFill className="fill-amber-500" size={22} />}
            title={t('budget.chart.currentObjective')}
            subtitle={`${DateTime.fromJSDate(goal.startDate).setLocale(currentLanguage).toLocaleString({
              month: 'short',
              year: 'numeric'
            })} ${t('budget.chart.dateTo')} ${DateTime.fromJSDate(goal.endDate)
              .setLocale(currentLanguage)
              .toLocaleString({ month: 'short', year: 'numeric' })}`}
            value={
              <>
                {formatNumber(goal.goal)} {GOAL_MEASURE_UNIT[goal.measure]}
              </>
            }
          />
          {toDate && (
            <ChartVignette
              icon={<GoSquareFill className="fill-sky-500" size={22} />}
              title={t('budget.chart.forecastedCosts')}
              subtitle={`${DateTime.fromJSDate(goal.startDate)
                .setLocale(currentLanguage)
                .toLocaleString({ month: 'short', year: 'numeric' })} ${t('budget.chart.toToday')}`}
              value={
                <>
                  {formatNumber(toDate.cumulativeCost)} {GOAL_MEASURE_UNIT[goal.measure]}
                </>
              }
            />
          )}
          {toDate && (
            <ChartVignette
              title={t('budget.chart.remainingBudget')}
              subtitle={`${remainingDays} ${t('budget.chart.remainingDays')}`}
              value={<>{formatNumber(toDate.remainingPercent)} %</>}
            />
          )}
        </ChartVignettes>

        <ComboChart
          showGridLines={false}
          showLegend={false}
          className="h-[400px] pt-10"
          data={chartData}
          index="date"
          barSeries={{
            yAxisWidth: 85,
            valueFormatter: (value) => `${formatNumber(value)} ${GOAL_MEASURE_UNIT[goal.measure]}`,
            categories: [t('budget.chart.forecastedCosts')],
            yAxisLabel:
              goal.measure === 'currency'
                ? t('budget.chart.costAxisLabel')
                : t('stations.leakSection.chart.yAxisLabel'),
            colors: ['sky', 'teal']
          }}
          lineSeries={{
            valueFormatter: (value) => `${formatNumber(value)} ${GOAL_MEASURE_UNIT[goal.measure]}`,
            categories: [t('budget.chart.objectiveCosts')],
            colors: ['amber']
          }}
        />
      </Flex>
    </SectionCard>
  )
}

export { BudgetChart }
